import { useState } from 'react';
import { useLanguage, useT } from '../i18n/i18n';
import { MEASURE_LABEL, deleteActivity, type Activity } from './api';
import ActivityEditor, { ACTIVITY_KIND_LABEL } from './ActivityEditor';
import type { AskConfirm } from './GoalCard';
import { formatDay } from './GoalDates';
import './extras.css';

export default function ActivityItem({ learnerId, activity, onChanged, ask }: {
  learnerId: string; activity: Activity; onChanged: () => void; ask: AskConfirm;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const { lang } = useLanguage();
  const [showHow, setShowHow] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const a = activity;

  if (editing) {
    return (
      <li className="planner-activity is-editing">
        <ActivityEditor learnerId={learnerId} targetId={a.targetId} activity={a} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged(); }} />
      </li>
    );
  }

  return (
    <li className="planner-activity">
      <div className="planner-activity-head">
        <span className="pill planner-activity-kind">{t(ACTIVITY_KIND_LABEL[a.kind])}</span>
        <strong className="planner-activity-title">{a.kind === 'PROGRAM' ? (a.programName ?? a.title) : a.title}</strong>
      </div>
      <dl className="planner-goal-meta">
        <div>
          <dt>{t('How it is recorded')}</dt>
          <dd>{a.kind === 'PROGRAM' ? t('Automatically, by the program') : t(MEASURE_LABEL[a.measure])}</dd>
        </div>
      </dl>
      {a.instructions && (
        <>
          <button type="button" className="btn ghost planner-activity-toggle" aria-expanded={showHow} onClick={() => setShowHow(!showHow)}>
            {showHow ? t('Hide instructions') : t('Show instructions')}
          </button>
          {showHow && <p className="planner-activity-instructions">{a.instructions}</p>}
        </>
      )}
      {a.changes.length > 0 && (
        <>
          <button type="button" className="btn ghost planner-activity-toggle" aria-expanded={showChanges} onClick={() => setShowChanges(!showChanges)}>
            {t('Changes: {n}', { n: a.changes.length })}
          </button>
          {showChanges && (
            <ul className="planner-tpl-changes">
              {a.changes.map((c, i) => (
                <li key={`${c.day}-${i}`}>
                  <span className="planner-tpl-change-day">{formatDay(c.day, lang)}</span>
                  <span>{c.summary}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={() => setEditing(true)}>{t('Edit')}</button>
        <button
          type="button"
          className="btn quiet-danger"
          onClick={() => ask({
            title: t('Remove this activity?'),
            message: t('"{name}" will be removed from this target. This cannot be undone.', { name: a.title }),
            confirmLabel: t('Remove'),
            run: () => deleteActivity(learnerId, a.id),
          })}
        >
          {t('Remove')}
        </button>
      </div>
    </li>
  );
}
